import { useState } from "react";
import { useApps, useCreatePolicy, usePolicies, useRemovePolicy } from "../api/hooks.js";
import { Empty, PageState } from "../components/PageState.js";
const LABELS: Record<string, string> = {
  DEVICE_PLAYTIME_DAILY: "Daily headset limit",
  GAME_PLAYTIME_DAILY: "Daily game limit",
  BEDTIME_WINDOW: "Bedtime",
  SCHOOL_HOMEWORK_BLACKOUT: "Homework time",
  BONUS_TIME: "Extra time",
  APP_DENYLIST: "Blocked apps",
  BREAK_AFTER_CONTINUOUS_USE: "Break reminder",
};
export function SchedulesPage({ deviceId }: { deviceId: string }) {
  const policies = usePolicies(deviceId);
  const apps = useApps(deviceId);
  const create = useCreatePolicy(deviceId);
  const remove = useRemovePolicy(deviceId);
  const [type, setType] = useState("DEVICE_PLAYTIME_DAILY");
  const [minutes, setMinutes] = useState("60");
  const [start, setStart] = useState("21:30");
  const [end, setEnd] = useState("06:30");
  const [pkg, setPkg] = useState("");
  const timed =
    type === "BEDTIME_WINDOW" || type === "SCHOOL_HOMEWORK_BLACKOUT";
  if (!deviceId) return <Empty label="Select a headset to plan playtime." />;
  return (
    <PageState query={policies}>
      <section className="card stack">
        <h2>Your household’s rhythm</h2>
        <p className="muted">
          Rules apply to everyone on this headset. Times use the household
          timezone: America/New_York.
        </p>
        <div className="grid">
          {(policies.data ?? [])
            .filter(
              (p) =>
                p.enabled &&
                (p.type !== "BONUS_TIME" ||
                  new Date(String(p.parameters.expires_at)).getTime() >
                    Date.now()),
            )
            .map((p) => (
              <article className="notice stack" key={p.id}>
                <strong>
                  {LABELS[p.type] ?? p.type.replaceAll("_", " ").toLowerCase()}
                </strong>
                <p>
                  {p.parameters.minutes
                    ? `${p.parameters.minutes} minutes`
                    : p.parameters.window
                      ? `${(p.parameters.window as { start: string; end: string }).start} – ${(p.parameters.window as { start: string; end: string }).end}`
                      : p.type === "SCHOOL_HOMEWORK_BLACKOUT"
                        ? "Scheduled homework hours"
                        : "Custom rule"}
                </p>
                <details>
                  <summary className="muted">Rule details</summary>
                  <p className="mono">{JSON.stringify(p.parameters)}</p>
                </details>
                <button className="secondary" disabled={remove.isPending}
                  onClick={() => remove.mutate(p.id)}>
                  {remove.isPending && remove.variables === p.id ? "Removing…" : "Remove schedule"}
                </button>
              </article>
            ))}
        </div>
        {remove.isSuccess && <p className="notice" role="status">Schedule removed. The headset will stop applying it after its next sync. Other rules still apply.</p>}
        {remove.error && <p className="notice danger" role="alert">Couldn’t remove the schedule. {remove.error.message}</p>}
        {!(policies.data ?? []).some((p) => p.enabled) && (
          <Empty label="No active schedules. Create your first one below." />
        )}
      </section>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          const parameters =
            type === "BEDTIME_WINDOW"
              ? { window: { start, end } }
              : type === "SCHOOL_HOMEWORK_BLACKOUT"
                ? { windows: [{ start, end }], recurring_dows: 31 }
                : type === "GAME_PLAYTIME_DAILY"
                  ? { minutes: Number(minutes), package_ids: [pkg] }
                  : { minutes: Number(minutes) };
          create.mutate({ type, parameters, enabled: true });
        }}
      >
        <h3>Add a playtime rule</h3>
        <p className="muted">
          New rules are added alongside existing ones. The most restrictive
          applicable rule still wins.
        </p>
        <label htmlFor="ptype">What would you like to set?</label>
        <select
          id="ptype"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          {[
            "DEVICE_PLAYTIME_DAILY",
            "GAME_PLAYTIME_DAILY",
            "BEDTIME_WINDOW",
            "SCHOOL_HOMEWORK_BLACKOUT",
          ].map((t) => (
            <option key={t} value={t}>
              {LABELS[t]}
            </option>
          ))}
        </select>
        {type === "GAME_PLAYTIME_DAILY" && (
          <>
            <label htmlFor="game">Choose a game</label>
            <select
              id="game"
              required
              value={pkg}
              onChange={(e) => setPkg(e.target.value)}
            >
              <option value="">Select a game</option>
              {(apps.data ?? []).map((a) => (
                <option key={a.package_id} value={a.package_id}>
                  {a.display_name ?? a.package_id.split(".").at(-1)}
                </option>
              ))}
            </select>
          </>
        )}
        {timed ? (
          <>
            <label htmlFor="start">Pause from</label>
            <input
              id="start"
              type="time"
              required
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
            <label htmlFor="end">Allow again at</label>
            <input
              id="end"
              type="time"
              required
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
            <p className="muted">
              {type === "SCHOOL_HOMEWORK_BLACKOUT"
                ? "Monday–Friday"
                : "Every day"}
              .{" "}
              {start === end
                ? "Matching start and end times block the full day."
                : "Overnight windows are supported."}
            </p>
          </>
        ) : (
          <>
            <label htmlFor="minutes">Minutes per day</label>
            <input
              id="minutes"
              type="number"
              min="1"
              max="1440"
              required
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </>
        )}
        <button disabled={create.isPending}>
          {create.isPending ? "Saving…" : "Save schedule"}
        </button>
        {create.isSuccess && (
          <p className="notice" role="status">
            Schedule saved. It will sync when the headset connects.
          </p>
        )}
        {create.error && (
          <p className="notice danger" role="alert">
            {create.error.message}
          </p>
        )}
      </form>
    </PageState>
  );
}
